import React from 'react';
import AircraftSchema from './AircraftSchema';
import AircraftPartSelector from './AircraftPartSelector';
import FlightSelector from './FlightSelector';
import LiquidTypeSelector from './LiquidTypeSelector';
import RequestSummary from './RequestSummary';
import { AIRCRAFT_PARTS } from '../types/aircraft';
import { useRequestForm } from '../hooks/useRequestForm';
import './CreateRequestModal.css';

interface CreateRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateRequestModal: React.FC<CreateRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess 
}) => {
  const {
    formData,
    flights,
    cars,
    statuses,
    users,
    loading,
    submitting,
    flightInputMode,
    manualFlightData,
    selectedAircraftParts,
    hoveredPart,
    aircraftView,
    handleInputChange,
    handleManualFlightChange,
    handleAircraftPartSelect,
    handlePartHover,
    handlePartLeave,
    handleFlightSelect,
    handleAircraftPartChange,
    handleViewChange,
    handleSubmit,
    setFlightInputMode,
    handleFlightsUpdate
  } = useRequestForm();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await handleSubmit();
    if (success) {
      onSuccess();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-contents">
        <div className="modal-header">
          <h2>Создать новую заявку</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <form onSubmit={onSubmit} className="modal-form">
          <div className="form-section">
            <h3>Основная информация</h3>
            
            <div className="form-group">
              <label htmlFor="title">Название заявки *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Введите название заявки"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Описание</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Введите описание заявки"
                rows={3}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Выбор машины и жидкости</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carId">Машина *</label>
                <select
                  id="carId"
                  name="carId"
                  value={formData.carId}
                  onChange={(e) => handleInputChange('carId', e.target.value)}
                  required
                >
                  <option value="">Выберите машину</option>
                  {cars.map((car) => (
                    <option key={car.id} value={car.id}>
                      {car.name} {car.model}
                    </option>
                  ))}
                </select>
              </div>

              <LiquidTypeSelector
                selectedLiquidType={formData.liquidType || ''}
                onLiquidTypeChange={(liquidType) => handleInputChange('liquidType', liquidType)}
              />
            </div>
          </div>

          <FlightSelector
            flightInputMode={flightInputMode}
            onFlightInputModeChange={setFlightInputMode}
            flights={flights}
            selectedFlightId={formData.flightId}
            onFlightSelect={handleFlightSelect}
            manualFlightData={manualFlightData}
            onManualFlightChange={handleManualFlightChange}
            onFlightsUpdate={handleFlightsUpdate}
          />

          <div className="form-section">
            <h3>Выбор части самолета</h3>
            
            <div className="aircraft-part-selection">
              <AircraftPartSelector
                aircraftView={aircraftView}
                selectedPartNames={formData.aircraftParts.map(part => part.aircraftPartName)}
                onPartChange={handleAircraftPartChange}
                aircraftParts={AIRCRAFT_PARTS}
              />

              <AircraftSchema
                aircraftView={aircraftView}
                selectedAircraftParts={selectedAircraftParts}
                hoveredPart={hoveredPart}
                onPartSelect={handleAircraftPartSelect}
                onPartHover={handlePartHover}
                onPartLeave={handlePartLeave}
                aircraftParts={AIRCRAFT_PARTS}
                onViewChange={handleViewChange}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Дополнительные настройки</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="responsibleId">Ответственный *</label>
                <select
                  id="responsibleId"
                  name="responsibleId"
                  value={formData.responsibleId}
                  onChange={(e) => handleInputChange('responsibleId', e.target.value)}
                  required
                >
                  <option value="">Выберите ответственного</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.surname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="statusId">Статус *</label>
                <select
                  id="statusId"
                  name="statusId"
                  value={formData.statusId}
                  onChange={(e) => handleInputChange('statusId', e.target.value)}
                  required
                >
                  <option value="">Выберите статус</option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="priority">Приоритет</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                >
                  <option value="LOW">Низкий</option>
                  <option value="MEDIUM">Средний</option>
                  <option value="HIGH">Высокий</option>
                </select>
              </div>
            </div>
          </div>

          <RequestSummary 
            formData={formData}
          />

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Отмена
            </button>
            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Создание...' : 'Создать заявку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRequestModal; 